import React from "react";
import { connect } from "react-redux";
import * as Survey from "survey-react";
import { Spinner } from "react-activity";
import Config from "../../config";
import { Message } from "../home/message";
import { translate } from "../../lib/intl";
import { setAlertDialog } from "../../components/alert/alert";
import { api } from "../../app";
import { authenticationActions } from "../../store/store";
import {
  clearDropdowns,
  getApiUrl,
  updateDependentFields,
} from "../../survey/survey.utils";
import { cooperative_assessment_rwanda_direct_model } from "./cooperative_assessment_rwanda_direct_model";
import "../../theme/survey.scss";
import "../home/home.switch.scss";

const CURRENT_CONTAINER = "cooperative_assessment";

class CooperativeAssessmentContainer extends React.PureComponent {
  constructor(props) {
    super(props);
    this.state = {
      pending: true,
      assessment: null,
      error: null,
      is_started: true,
      submitted: false,
    };
    this.uuid = props.match.params.uuid;
    this.token = props.match.params.token;
  }

  // Create the model only after login: SurveyJS requests choicesByUrl when the
  // model is created, and the auth header for those requests is set on login.
  createModel = () => {
    this.model = new Survey.Model(
      cooperative_assessment_rwanda_direct_model(getApiUrl(this.props))
    );

    if (Config.dropdowns_to_clear?.[CURRENT_CONTAINER]) {
      clearDropdowns(this.model, Config.dropdowns_to_clear[CURRENT_CONTAINER]);
    }

    const dependentQuestions = Config.dependent_questions?.[CURRENT_CONTAINER];
    if (dependentQuestions) {
      updateDependentFields(this.model, dependentQuestions);
    }

    this.model.onPartialSend.add((survey) => this.onPartialSend(survey));
    this.model.onCompleting.add((survey, options) =>
      this.onCompleting(survey, options)
    );
    this.model.onValueChanged.add((survey) => {
      if (survey.isLastPage && !this.isCompleting) {
        this.onPartialSend(survey);
      }
    });
  };

  componentDidMount() {
    if (
      this.props.device.isAuthenticated &&
      this.props.device.token.access_token === this.token
    ) {
      this.loadAssessment();
    } else {
      this.props.loginWithAccessToken(this.token).then((profile) => {
        if (profile) {
          this.loadAssessment();
        } else {
          this.setState({ pending: false, error: "not_authorized" });
        }
      });
    }
  }

  loadAssessment = async () => {
    try {
      const response = await api.get(
        `cooperative-assessment/${this.uuid}/view.json`
      );
      // API helpers in this codebase return payload directly on 200,
      // and only include error keys on failures.
      if (!response || response.error) {
        this.setState({ pending: false, error: response?.error || "not_found" });
        return;
      }
      const assessment = response.data || response;
      this.createModel();
      // Load saved responses before rendering: custom widgets (phone, file_uploader)
      // read question.value only once, when they are first rendered.
      this.model.setVariable("cooperative_name", assessment.cooperative_name || "");
      this.model.data = assessment.responses || {};
      this.setState({ pending: false, assessment, is_started: true });
    } catch (error) {
      this.setState({ pending: false, error: "not_found" });
    }
  };

  saveAssessment = async (responses, is_completed = false) => {
    const { cooperative_name, ...payload } = responses;
    return api.post("cooperative-assessment/update_assessment.json", {
      uuid: this.uuid,
      responses: payload,
      is_completed,
    });
  };

  onPartialSend = async (survey) => {
    try {
      await this.saveAssessment(survey.data, false);
    } catch (error) {
      // Silently ignore background partial save failures
    }
  };

  onCompleting = async (survey, options) => {
    if (this.isCompleting) return;
    this.isCompleting = true;

    if (options) {
      options.allowComplete = false;
    }
    try {
      const response = await this.saveAssessment(survey.data, true);

      if (!response || response.error) {
        this.isCompleting = false;

        const errorMessage = response && typeof response.error === "string"
          ? translate("errors." + response.error)
          : translate("errors.server_error");
        setAlertDialog(translate("errors.title"), errorMessage);
        return;
      }

      const assessment = response.data || response;
      if (assessment) {
        survey.doComplete();
        this.setState({ assessment, submitted: true });
        this.isCompleting = false;
      } else {
        this.isCompleting = false;
      }
    } catch (error) {
      this.isCompleting = false;
    }
  };

  render() {
    if (this.state.pending) {
      return (
        <div className="loadingSpinner">
          <Spinner color="#eee" size={50} speed={1} />
        </div>
      );
    }

    if (this.state.error) {
      return (
        <div className="message center bigSpacer">
          <h2>{translate("sorry_not_found.header")}</h2>
          <p>{translate("sorry_not_found.desc")}</p>
        </div>
      );
    }

    if (this.state.submitted) {
      return (
        <div className="homeContainer">
          <Message message="submitted_cooperative" />
        </div>
      );
    }

    return (
      <div className="centerWrapper">
        <Survey.Survey model={this.model} locale={this.props.device.locale} />
      </div>
    );
  }
}

const mapStateToProps = (state) => ({
  device: state.device,
});

const mapDispatchToProps = (dispatch) => ({
  loginWithAccessToken: (accessToken) =>
    dispatch(authenticationActions.loginWithAccessToken(accessToken)),
});

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(CooperativeAssessmentContainer);
